import Typography from '@mui/material/Typography';
import { Checkbox } from '@openx/components-core/lib/Checkbox/Checkbox';
import { theme } from '@openx/components-core/lib/theme/theme';
import { isValidElement, memo, type ReactNode, useId } from 'react';
import type { BaseTargetingPresetOptionProps } from '../utils/types';
import { StyledTargetingPresetButton } from './TargetingPresetButton';

const deriveLabelFromNode = (labelNode: ReactNode): string =>
  labelNode == null || typeof labelNode === 'boolean'
    ? ''
    : typeof labelNode === 'string' || typeof labelNode === 'number'
      ? String(labelNode)
      : Array.isArray(labelNode)
        ? labelNode.map(deriveLabelFromNode).join(' ')
        : isValidElement<{ children?: ReactNode }>(labelNode)
          ? deriveLabelFromNode(labelNode.props?.children)
          : '';

const cleanLabel = (label: string) =>
  label
    .trim()
    .replace(/\s*\(\d+\)$/, '')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase();

export const TargetingPresetOption = memo(
  ({
    label,
    icon,
    isSelected = false,
    isDisabled = false,
    isError = false,
    isSubOption = false,
    isDropdownOpen = false,
    onClick,
    customCheckbox,
  }: BaseTargetingPresetOptionProps) => {
    const checkboxChecked = customCheckbox?.checked ?? isSelected;
    const checkboxIndeterminate = customCheckbox?.indeterminate ?? false;
    const checkboxOnClick = customCheckbox?.onClick;

    const labelContent = deriveLabelFromNode(label);
    const labelText = cleanLabel(labelContent);

    const uniqueIdFallback = useId();
    const dataTestId = labelText ? labelText : `deal-preset-option-${uniqueIdFallback}`;

    return (
      <StyledTargetingPresetButton
        variant="outlined"
        onClick={onClick}
        data-test={`${dataTestId}-button${isError ? '-error' : ''}`}
        disabled={isDisabled && !isSelected}
        isSubOption={isSubOption}
        isSelected={isSelected}
        isDropdownOpen={isDropdownOpen}
        isError={isError}
        isDisabled={isDisabled}
      >
        <Checkbox
          checked={checkboxChecked}
          indeterminate={checkboxIndeterminate}
          onClick={checkboxOnClick}
          data-test={`${dataTestId}-checkbox`}
          disabled={isDisabled}
          sx={{
            '&.Mui-disabled .MuiSvgIcon-root': {
              color: theme.palette.background.boxDarker,
            },
          }}
        />

        {icon}

        {label && (
          <Typography variant="body2Bold" component="span">
            {label}
          </Typography>
        )}
      </StyledTargetingPresetButton>
    );
  }
);
